import { useCallback } from 'react'
import { useLatest } from 'react-use'
import { Vector3 } from 'three'
import { useControlStore } from '@editorStores'
import {
  useGetIfcSelectionGuids,
  useIfcSelection,
  useSceneContextMenuAction,
  useUpdateIfcModelMetadata,
} from '@editorHooks'

/**
 * Registers the scene right-click "Filtern" action for the guided IFC workflow so filtering is
 * available in every step (not only the filter step). Adds the right-clicked IFC element — or the
 * whole current selection when the clicked element is part of it — to the model's filtered
 * elements. Context-menu callbacks are captured once, so all live values are read through refs.
 */
const useIFCGuidedFilterContextMenu = (enabled: boolean) => {
  const activeIfcFileGuid = useControlStore(state => state.activeIfcFileGuid)
  const { setSelectedIfcElementGuids } = useIfcSelection()
  const getIfcSelectionGuids = useGetIfcSelectionGuids()

  const { hasIfcModelMetadata, updateIfcModelMetadata } =
    useUpdateIfcModelMetadata(activeIfcFileGuid)

  const enabledLatest = useLatest(enabled && hasIfcModelMetadata)
  const updateIfcModelMetadataLatest = useLatest(updateIfcModelMetadata)

  const handleFilter = useCallback(
    (selectedElement: string | null) => {
      if (!selectedElement) return

      const selection = getIfcSelectionGuids()
      const isInSelection = selection.includes(selectedElement)
      const guids = isInSelection ? selection : [selectedElement]

      updateIfcModelMetadataLatest.current(current => {
        if (!current) return current
        return {
          ...current,
          filteredElements: [...(current.filteredElements ?? []), guids],
        }
      })

      if (isInSelection) setSelectedIfcElementGuids([])
    },
    [getIfcSelectionGuids, updateIfcModelMetadataLatest, setSelectedIfcElementGuids],
  )

  const isAvailable = useCallback(
    (
      selectedElement: string | null,
      point: Vector3 | null,
      userDataActions: Record<string, unknown>,
    ) => enabledLatest.current && !!selectedElement && 'filter' in userDataActions,
    [enabledLatest],
  )

  useSceneContextMenuAction({
    label: 'Filtern',
    action: handleFilter,
    available: isAvailable,
    group: 'default',
  })
}

export default useIFCGuidedFilterContextMenu
